import React, { useState, useCallback, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { cn } from '../lib/utils';

export const Layout = ({
    title,
    subtitle,
    children,
    hideSidebar = false,
    sidebarCollapsed = false,
    onSidebarMouseEnter,
    onSidebarMouseLeave,
    noPadding = false,
    hideHeader = false,
    ...props
}) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const toggleMobileMenu = useCallback(() => {
        setIsMobileMenuOpen(prev => !prev);
    }, []);

    const closeMobileMenu = useCallback(() => {
        setIsMobileMenuOpen(false);
    }, []);

    // Close mobile menu on window resize to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024) {
                setIsMobileMenuOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMobileMenuOpen]);

    // hideSidebar  -> do not render sidebar at all (legacy behavior)
    // sidebarCollapsed -> render sidebar but animate it collapsed
    return (
        <div className="app-layout" data-testid="app-layout">
            {!hideSidebar && (
                <>
                    {/* Desktop sidebar - always visible inline at >=1024px */}
                    <div
                        className={cn(
                            'sidebar-wrapper',
                            sidebarCollapsed && 'sidebar-wrapper--collapsed',
                        )}
                        aria-hidden={sidebarCollapsed}
                        onMouseEnter={onSidebarMouseEnter}
                        onMouseLeave={onSidebarMouseLeave}
                    >
                        <Sidebar />
                    </div>

                    {/* Mobile backdrop - only renders when menu is open */}
                    {isMobileMenuOpen && (
                        <div
                            className="mobile-sidebar-backdrop lg:hidden fixed inset-0 bg-black/40 z-40"
                            onClick={closeMobileMenu}
                            aria-hidden="true"
                        />
                    )}

                    {/* Mobile sidebar drawer - always in DOM for animation, slides in/out */}
                    <div
                        className={cn(
                            'mobile-sidebar-drawer lg:hidden fixed top-0 left-0 h-full z-50 transition-transform duration-300 ease-in-out',
                            isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
                        )}
                    >
                        <Sidebar closeMobileMenu={closeMobileMenu} isMobileMenuOpen={isMobileMenuOpen} />
                    </div>
                </>
            )}
            <div className="main-content">
                {!hideHeader && (
                    <Header
                        title={title}
                        subtitle={subtitle}
                        isMobileMenuOpen={isMobileMenuOpen}
                        onToggleMobileMenu={toggleMobileMenu}
                    />
                )}
                <main className={cn('content-area', noPadding && '!p-0', props.className)}>
                    {children || <Outlet />}
                </main>
            </div>
        </div>
    );
};

export default Layout;